import {useQuery} from '@tanstack/react-query';
import useWorkflowStore from '../store';
import {apiService} from '@/lib/ApiService';
import {defaultQueryOptions} from '@/pages/_utils';
import {BaseKey} from '@refinedev/core';
import {useEffect} from 'react';
import {newNode} from '../nodes/utils';
import {normalize_workflow_nodes} from '../utils/auto_calculate_dates';

///////// load workflow of project and tasks (nested)
export const useLoadWorkflow = (project_id?: BaseKey) => {
  const {
    setProjectData,
    setProjectId,
    setNodes,
    setEdges,
    setViewport,
    setNoNodeTasks,
    setTimelineMarkers,
    clearChanges,
    clearData,
    takeSnapshot,
  } = useWorkflowStore();
  // load data
  const loadWorkflow = useQuery({
    queryKey: ['workflow-query', 'projects', String(project_id) || ''],
    queryFn: async () => {
      if (!project_id) return;
      return await apiService.projects.loadWorkflow(project_id);
    },
    ...defaultQueryOptions,
  });

  // load task list, update data for nodes
  useEffect(() => {
    if (!project_id || !loadWorkflow.data) return;
    clearData();

    const taskList = loadWorkflow.data?.tasks; //useTaskList.data?.data || [];
    const project = loadWorkflow.data;
    const workflow = loadWorkflow.data?.workflow;
    const nodes = workflow?.nodes || [];
    const edges = workflow?.edges || [];

    setProjectId(project_id);
    setProjectData(project);

    // timeline markers
    setTimelineMarkers(workflow?.timelineMarkers || []);

    // update edges
    setEdges(edges);

    // update viewport
    if (workflow?.viewport) setViewport(workflow.viewport);

    // update nodes with task data

    nodes.forEach(node => {
      if (node.type === 'task') {
        const task = taskList.find(t => t.uuid === node.id);
        if (task) {
          node.data = {
            ...node.data,
            ...task,
            start_date: task.start_date ? new Date(task.start_date) : null,
            initial_end_date: task.initial_end_date ? new Date(task.initial_end_date) : null,
            actual_end_date: task.actual_end_date ? new Date(task.actual_end_date) : null,
            rescheduled_end_date: task.rescheduled_end_date
              ? new Date(task.rescheduled_end_date)
              : null,
          };
        } else {
          // remove node if task not found
          node.data.name = `(Deleted)`;
        }
      }
    });

    // check if start node is missing
    let start_node = nodes.find(n => n.type === 'start');
    if (!start_node) {
      start_node = newNode({
        type: 'start',
        position: {x: 0, y: 0},
        data: {start_date: project.start_date},
      });
      nodes.push(start_node);
    }
    // make sure start_date is project start date
    start_node.data = {...start_node.data, start_date: project.start_date};

    // setNodes(nodes);
    // no node tasks are tasks that are not in the workflow and do not have parent
    setNoNodeTasks(taskList.filter(t => !t.parent_id && !nodes.some(n => n.id === t.uuid)));

    // calculate task node dates
    // const {newNodes} = auto_calculate_dates(start_node, nodes, edges);
    // setNodes(newNodes);

    const {nodes: newNodes, edges: newEdges} = normalize_workflow_nodes(
      nodes.filter(n => n.data.name != '(Deleted)'),
      edges,
    );
    setNodes(newNodes);
    setEdges(newEdges);

    // clear dirty objects because it is newly loaded
    clearChanges();

    // marked loaded
    console.log('workflow loaded', workflow, taskList);
    takeSnapshot(); //save initial state
    // setLoaded(true);
  }, [loadWorkflow.data, project_id]);

  return {loadWorkflow, tasks: loadWorkflow.data?.tasks || []};
};
